import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  ArrowLeft,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Download,
  Share2,
  RefreshCw,
  TrendingUp,
  Activity,
  FileText,
  Target,
  ChevronRight,
  Menu,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";

import { useParams, useNavigate } from "@tanstack/react-router";
import { cn } from "../lib/utils";

interface TestRunDetails {
  id: string;
  name: string;
  testSuite: string;
  agent: string;
  status: "running" | "completed" | "failed" | "scheduled" | "cancelled";
  startTime: Date;
  endTime?: Date;
  duration?: string;
  progress: number;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  anomalies: number;
  trigger: "manual" | "scheduled" | "webhook";
  source: "github_pr" | "manual" | "api" | "scheduled" | "webhook";
  sourceDetails?: {
    prNumber?: string;
    branch?: string;
    commit?: string;
    repository?: string;
  };
  metrics: {
    avgResponseTime: number;
    maxResponseTime: number;
    minResponseTime: number;
    totalRequests: number;
    successRate: number;
    errorRate: number;
  };
  tests: TestDetails[];
  insights: Insight[];
  logs: LogEntry[];
}

interface TestDetails {
  id: string;
  name: string;
  status: "passed" | "failed" | "running" | "pending";
  duration: number;
  category: string;
  description: string;
  errorMessage?: string;
  metrics: {
    responseTime: number;
    accuracy: number;
    confidence: number;
    tokensUsed: number;
  };
  logs: LogEntry[];
  output: {
    input: string;
    expectedOutput: string;
    actualOutput: string;
    prompt: string;
    response: string;
    metadata: {
      model: string;
      temperature: number;
      maxTokens: number;
      stopSequences: string[];
    };
  };
}

interface LogEntry {
  timestamp: Date;
  level: "info" | "warn" | "error" | "debug";
  message: string;
  data?: any;
}

interface Insight {
  type: "performance" | "accuracy" | "anomaly" | "trend";
  title: string;
  description: string;
  severity: "low" | "medium" | "high" | "critical";
  metric: string;
  value: number;
  previousValue?: number;
  change?: number;
}

// Sample data for the test run details
const sampleTestRunDetails: TestRunDetails = {
  id: "1",
  name: "Conversation Flow Test Run #1247",
  testSuite: "Conversation Flow Tests",
  agent: "Beauty Sales Agent",
  status: "running",
  startTime: new Date(Date.now() - 2 * 60 * 1000), // 2 minutes ago
  progress: 75,
  totalTests: 156,
  passedTests: 117,
  failedTests: 0,
  anomalies: 2,
  trigger: "scheduled",
  source: "github_pr",
  sourceDetails: {
    prNumber: "PR #1234",
    branch: "feature/new-conversation-flow",
    commit: "abc123",
    repository: "rippletide/ai-testing",
  },
  metrics: {
    avgResponseTime: 245,
    maxResponseTime: 890,
    minResponseTime: 120,
    totalRequests: 1247,
    successRate: 94.2,
    errorRate: 5.8,
  },
  tests: [
    {
      id: "test-1",
      name: "Skincare Consultation Flow",
      status: "passed",
      duration: 1200,
      category: "Customer Consultation",
      description:
        "Tests luxury skincare consultation and product recommendation flow",
      metrics: {
        responseTime: 1.8,
        accuracy: 98.5,
        confidence: 0.92,
        tokensUsed: 156,
      },
      logs: [
        { timestamp: new Date(), level: "info", message: "Test started" },
        {
          timestamp: new Date(),
          level: "info",
          message: "Skincare consultation initiated",
          data: { input: "Hello, I need help with anti-aging products" },
        },
        {
          timestamp: new Date(),
          level: "info",
          message: "Prompt sent to model",
          data: { model: "gpt-4", temperature: 0.7 },
        },
        {
          timestamp: new Date(),
          level: "info",
          message: "Response received",
          data: { responseTime: 1.8, tokensUsed: 156 },
        },
        {
          timestamp: new Date(),
          level: "info",
          message: "Luxury consultation completed successfully",
        },
      ],
      output: {
        input: "Hello, I need help with anti-aging products",
        expectedOutput:
          "Welcome to Beauty! I'd be delighted to help you with our luxury anti-aging collection. Our Advanced Génifique line is perfect for addressing aging concerns. What's your skin type and main concerns?",
        actualOutput:
          "Welcome to Beauty! I'd be delighted to help you with our luxury anti-aging collection. Our Advanced Génifique line is perfect for addressing aging concerns. What's your skin type and main concerns?",
        prompt:
          "You are a Beauty luxury cosmetics sales agent. A customer says: 'Hello, I need help with anti-aging products'. Respond with a welcoming, luxury-focused greeting and ask about their skin type and concerns.",
        response:
          "Welcome to Beauty! I'd be delighted to help you with our luxury anti-aging collection. Our Advanced Génifique line is perfect for addressing aging concerns. What's your skin type and main concerns?",
        metadata: {
          model: "gpt-4",
          temperature: 0.7,
          maxTokens: 150,
          stopSequences: ["\n\n", "Human:", "Assistant:"],
        },
      },
    },
    {
      id: "test-2",
      name: "Product Recommendation Engine",
      status: "running",
      duration: 800,
      category: "Product Knowledge",
      description:
        "Tests the AI's ability to recommend luxury products based on customer needs",
      metrics: {
        responseTime: 2.1,
        accuracy: 0,
        confidence: 0,
        tokensUsed: 89,
      },
      logs: [
        { timestamp: new Date(), level: "info", message: "Test started" },
        {
          timestamp: new Date(),
          level: "info",
          message: "Product recommendation in progress",
          data: { input: "I have dry skin and want something for fine lines" },
        },
        {
          timestamp: new Date(),
          level: "info",
          message: "Sending recommendation request",
          data: { model: "gpt-4", temperature: 0.3 },
        },
      ],
      output: {
        input: "I have dry skin and want something for fine lines",
        expectedOutput:
          "For dry skin with fine lines, I recommend our Absolue Precious Cells cream with rose extract and our Advanced Génifique serum",
        actualOutput: "",
        prompt:
          "Recommend Beauty products for a customer with dry skin and fine lines. Focus on luxury positioning and specific product benefits.",
        response: "",
        metadata: {
          model: "gpt-4",
          temperature: 0.3,
          maxTokens: 100,
          stopSequences: ["\n", "Human:", "Assistant:"],
        },
      },
    },
    {
      id: "test-3",
      name: "Order Status Inquiry",
      status: "failed",
      duration: 2100,
      category: "Customer Service",
      description: "Tests order status lookup and customer service accuracy",
      errorMessage: "Order lookup failed for invalid order numbers",
      metrics: {
        responseTime: 2.5,
        accuracy: 75.0,
        confidence: 0.65,
        tokensUsed: 234,
      },
      logs: [
        { timestamp: new Date(), level: "info", message: "Test started" },
        {
          timestamp: new Date(),
          level: "info",
          message: "Order status inquiry initiated",
          data: {
            input: "Where is my order #99999?",
          },
        },
        {
          timestamp: new Date(),
          level: "warn",
          message: "Order lookup taking longer than expected",
          data: { elapsedTime: 1500, maxTimeout: 2000 },
        },
        {
          timestamp: new Date(),
          level: "error",
          message: "Order lookup failed",
          data: { totalTime: 2100, maxTimeout: 2000, error: "Order not found" },
        },
      ],
      output: {
        input: "Where is my order #99999?",
        expectedOutput:
          "I can see your order #99999 is currently being processed and will ship within 2-3 business days. You'll receive a tracking email once it ships.",
        actualOutput:
          "I can't find that order number in our system. Could you please verify the order number or provide your email address so I can look it up?",
        prompt:
          "A customer asks about order #99999. Provide a helpful response about order status or ask for verification if order not found.",
        response:
          "I can't find that order number in our system. Could you please verify the order number or provide your email address so I can look it up?",
        metadata: {
          model: "gpt-4",
          temperature: 0.8,
          maxTokens: 200,
          stopSequences: ["\n\n", "Human:", "Assistant:"],
        },
      },
    },
    {
      id: "test-4",
      name: "Luxury Brand Positioning",
      status: "pending",
      duration: 0,
      category: "Brand Experience",
      description:
        "Tests the AI's ability to maintain luxury brand positioning and tone",
      metrics: {
        responseTime: 0,
        accuracy: 0,
        confidence: 0,
        tokensUsed: 0,
      },
      logs: [],
      output: {
        input: "",
        expectedOutput: "",
        actualOutput: "",
        prompt: "",
        response: "",
        metadata: {
          model: "gpt-4",
          temperature: 0.7,
          maxTokens: 150,
          stopSequences: ["\n\n", "Human:", "Assistant:"],
        },
      },
    },
  ],
  insights: [
    {
      type: "performance",
      title: "Luxury Consultation Response Time",
      description:
        "Skincare consultation responses are 12% faster than previous runs, improving customer experience",
      severity: "low",
      metric: "avgResponseTime",
      value: 1.8,
      previousValue: 2.1,
      change: -12,
    },
    {
      type: "anomaly",
      title: "Product Recommendation Accuracy Drop",
      description:
        "Luxury product recommendations showing 15% lower accuracy in dry skin scenarios",
      severity: "high",
      metric: "accuracy",
      value: 75.0,
      previousValue: 90.2,
      change: -15,
    },
    {
      type: "accuracy",
      title: "Brand Positioning Consistency",
      description:
        "Luxury brand tone and positioning maintained with 98.5% accuracy",
      severity: "low",
      metric: "accuracy",
      value: 98.5,
      previousValue: 96.8,
      change: 2,
    },
  ],
  logs: [
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000),
      level: "info",
      message: "Luxury consultation test run started",
      data: {
        testSuite: "Beauty Customer Experience Tests",
        agent: "Beauty Sales Agent",
      },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 30 * 1000),
      level: "info",
      message: "Initializing luxury brand environment",
      data: { environment: "staging", version: "v2.1.3", brandTone: "luxury" },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 60 * 1000),
      level: "info",
      message: "Running 156 luxury customer experience tests",
      data: {
        totalTests: 156,
        parallelWorkers: 4,
        categories: ["Skincare", "Fragrances", "Customer Service"],
      },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 90 * 1000),
      level: "info",
      message: "Test 1: Skincare Consultation Flow - PASSED",
      data: {
        testId: "test-1",
        duration: 1200,
        accuracy: 98.5,
        luxuryScore: 95,
      },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 120 * 1000),
      level: "info",
      message: "Test 2: Product Recommendation Engine - RUNNING",
      data: {
        testId: "test-2",
        elapsedTime: 800,
        productCategory: "Anti-aging",
      },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 150 * 1000),
      level: "warn",
      message: "Test 3: Order Status Inquiry - TIMEOUT",
      data: {
        testId: "test-3",
        maxTimeout: 2000,
        actualTime: 2100,
        orderType: "Luxury Products",
      },
    },
    {
      timestamp: new Date(Date.now() - 2 * 60 * 1000 + 180 * 1000),
      level: "error",
      message: "Test 3: Order Status Inquiry - FAILED",
      data: {
        testId: "test-3",
        error: "Order lookup failed for invalid order numbers",
        customerImpact: "High",
      },
    },
    {
      timestamp: new Date(Date.now() - 1 * 60 * 1000),
      level: "info",
      message: "Progress: 75% complete (117/156 luxury tests)",
      data: {
        passed: 117,
        failed: 0,
        running: 39,
        total: 156,
        avgLuxuryScore: 92,
      },
    },
  ],
};

// Helper function for test status colors
const getTestStatusColor = (status: string) => {
  switch (status) {
    case "passed":
      return "bg-green-900/30 text-green-400 border-green-600/30";
    case "failed":
      return "bg-red-900/30 text-red-400 border-red-600/30";
    case "running":
      return "bg-blue-900/30 text-blue-400 border-blue-600/30";
    case "pending":
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    default:
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
  }
};

const TestRunDetailsPage: React.FC = () => {
  const { testRunId } = useParams({ from: "/test-runs/$testRunId" });
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
  const [showFullLogs, setShowFullLogs] = useState<Set<string>>(new Set());
  const [showFullOutput, setShowFullOutput] = useState<Set<string>>(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // In a real app, you would fetch the test run details based on testRunId
  const testRun = sampleTestRunDetails;

  const toggleTestExpansion = (testId: string) => {
    const newExpanded = new Set(expandedTests);
    if (newExpanded.has(testId)) {
      newExpanded.delete(testId);
    } else {
      newExpanded.add(testId);
    }
    setExpandedTests(newExpanded);
  };

  const toggleFullLogs = (testId: string) => {
    const newShowFullLogs = new Set(showFullLogs);
    if (newShowFullLogs.has(testId)) {
      newShowFullLogs.delete(testId);
    } else {
      newShowFullLogs.add(testId);
    }
    setShowFullLogs(newShowFullLogs);
  };

  const toggleFullOutput = (testId: string) => {
    const newShowFullOutput = new Set(showFullOutput);
    if (newShowFullOutput.has(testId)) {
      newShowFullOutput.delete(testId);
    } else {
      newShowFullOutput.add(testId);
    }
    setShowFullOutput(newShowFullOutput);
  };

  const navigate = useNavigate();

  const handleTestClick = (test: TestDetails) => {
    // Navigate to the test details page
    navigate({
      to: "/test-runs/$testRunId/test/$testId",
      params: { testRunId, testId: test.id },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      case "completed":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      case "failed":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "scheduled":
        return "bg-purple-900/30 text-purple-400 border-purple-600/30";
      case "cancelled":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return <Play className="h-4 w-4" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <XCircle className="h-4 w-4" />;
      case "scheduled":
        return <Clock className="h-4 w-4" />;
      case "cancelled":
        return <Pause className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getInsightSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "high":
        return "bg-orange-900/30 text-orange-400 border-orange-600/30";
      case "medium":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      case "low":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(testRun.tests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTests = testRun.tests.slice(startIndex, endIndex);

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.history.back()}
                className="text-gray-400 hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                  {testRun.name}
                </h1>
                <p className="text-sm text-gray-400">
                  {testRun.testSuite} • {testRun.agent}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900 overflow-auto">
          <div className="p-4 lg:p-6 space-y-6">
            {/* KPIs Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    Success Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-green-400">
                      {testRun.metrics.successRate}%
                    </div>
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  </div>
                  <Progress
                    value={testRun.metrics.successRate}
                    className="mt-2"
                  />
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    Avg Response Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-blue-400">
                      {testRun.metrics.avgResponseTime}ms
                    </div>
                    <Activity className="h-4 w-4 text-blue-400" />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Min: {testRun.metrics.minResponseTime}ms • Max:{" "}
                    {testRun.metrics.maxResponseTime}ms
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    Tests Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-pink-400">
                      {testRun.passedTests + testRun.failedTests}/
                      {testRun.totalTests}
                    </div>
                    <Target className="h-4 w-4 text-pink-400" />
                  </div>
                  <Progress
                    value={
                      ((testRun.passedTests + testRun.failedTests) /
                        testRun.totalTests) *
                      100
                    }
                    className="mt-2"
                  />
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    Anomalies Detected
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-orange-400">
                      {testRun.anomalies}
                    </div>
                    <AlertTriangle className="h-4 w-4 text-orange-400" />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {testRun.anomalies > 0
                      ? "Requires attention"
                      : "No issues detected"}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Insights Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-100">
                  AI Insights
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Automated analysis and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {testRun.insights.map((insight, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedInsight === insight.title
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-750 border-gray-700 hover:bg-gray-700"
                      }`}
                      onClick={() =>
                        setSelectedInsight(
                          selectedInsight === insight.title
                            ? null
                            : insight.title,
                        )
                      }
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge
                              className={`${getInsightSeverityColor(insight.severity)} border`}
                            >
                              {insight.severity}
                            </Badge>
                            <span className="text-sm text-gray-400 capitalize">
                              {insight.type}
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-100 mb-1">
                            {insight.title}
                          </h4>
                          <p className="text-sm text-gray-400">
                            {insight.description}
                          </p>
                          {selectedInsight === insight.title && (
                            <div className="mt-3 p-3 bg-gray-750 rounded border border-gray-600">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-400">
                                    Current Value:
                                  </span>
                                  <span className="ml-2 text-gray-100">
                                    {insight.value}
                                  </span>
                                </div>
                                {insight.previousValue && (
                                  <div>
                                    <span className="text-gray-400">
                                      Previous Value:
                                    </span>
                                    <span className="ml-2 text-gray-100">
                                      {insight.previousValue}
                                    </span>
                                  </div>
                                )}
                                {insight.change && (
                                  <div className="col-span-2">
                                    <span className="text-gray-400">
                                      Change:
                                    </span>
                                    <span
                                      className={`ml-2 ${insight.change > 0 ? "text-red-400" : "text-green-400"}`}
                                    >
                                      {insight.change > 0 ? "+" : ""}
                                      {insight.change}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <ChevronRight
                          className={`h-4 w-4 text-gray-400 transition-transform ${
                            selectedInsight === insight.title ? "rotate-90" : ""
                          }`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tests List */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-100">
                  Test Results
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {testRun.totalTests} tests • {testRun.passedTests} passed •{" "}
                  {testRun.failedTests} failed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {currentTests.map((test) => (
                    <div
                      key={test.id}
                      className="border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer"
                      onClick={() => handleTestClick(test)}
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Badge
                              className={`${getTestStatusColor(test.status)} border`}
                            >
                              {test.status}
                            </Badge>
                            <div>
                              <h4 className="font-medium text-gray-100">
                                {test.name}
                              </h4>
                              <p className="text-sm text-gray-400">
                                {test.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-sm text-gray-300">
                                {test.duration}ms
                              </div>
                              <div className="text-xs text-gray-400">
                                {test.category}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                Click to view conversation
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="text-sm text-gray-400">
                        Showing {startIndex + 1} to{" "}
                        {Math.min(endIndex, testRun.tests.length)} of{" "}
                        {testRun.tests.length} tests
                      </div>

                      <div className="flex items-center justify-center sm:justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>

                        <div className="flex items-center space-x-1">
                          {Array.from(
                            { length: totalPages },
                            (_, i) => i + 1,
                          ).map((page) => (
                            <Button
                              key={page}
                              variant={
                                page === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className={
                                page === currentPage
                                  ? "bg-pink-400 hover:bg-pink-500 text-white"
                                  : "border-gray-600 text-gray-300 hover:bg-gray-700"
                              }
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TestRunDetailsPage;
